import { TextField } from '@mui/material';
import {
  EditAppBar,
  FieldValidator,
  NonEditableField,
  PageWithDrawer,
  SimpleSelect,
  getRequiredErrorMessage,
  objectsAreDifferents,
} from '@prismamedia/one-components';
import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  ArticleFormat,
  BrandKey,
  CreateQualifierMutation,
  GetQualifierQuery_query_qualifier_Qualifier,
  QualifierStatus,
  QualifierType,
} from '../../../__generated__/queries-web';
import { replaceParams } from '../../../routing/Router';
import { paths } from '../../../routing/Router/paths';
import { useGlobalBrandKey } from '../../../utils/globalState';
import { validateSlug } from '../../../utils/slugValidation';
import { useGetQualifier } from './getQualifier.web.graphql';
import { useStyles } from './styles';

import {
  useCreateQualifier,
  useUpdateQualifier,
} from './updateQualifier.web.graphql';

const getEmptyItem = (
  brandKey: BrandKey,
): GetQualifierQuery_query_qualifier_Qualifier => ({
  __typename: 'Qualifier',
  id: '',
  brandKey,
  format: ArticleFormat.Rich,
  title: '',
  slug: '',
  description: null,
  status: QualifierStatus.Draft,
  type: QualifierType.Statistical,
});

const toDescriptionInput = (description?: string | null) => {
  const trimmed = description?.trim();
  return trimmed ? trimmed : null;
};

export const QualifierEditPage: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [brandKey] = useGlobalBrandKey();
  const [item, setItem] = useState<
    GetQualifierQuery_query_qualifier_Qualifier | undefined
  >(undefined);
  const { id } = useParams();
  const isCreate = id === 'new';
  const { data, loading, error } = useGetQualifier(id, isCreate);
  const [saveLoading, setSaveLoading] = useState(false);
  const updateItem = useUpdateQualifier();
  const createItem = useCreateQualifier();
  const saveMandatoryFields: FieldValidator<
    GetQualifierQuery_query_qualifier_Qualifier
  >[] = [
    {
      label: 'Titre',
      validate: ({ title }) => !!title,
    },
    {
      label: 'Slug',
      validate: ({ slug }) => (isCreate ? !!slug && !validateSlug(slug) : true),
    },
    {
      label: 'Statut',
      validate: ({ status }) => !!status,
    },
  ];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const emptyItem = useMemo(() => getEmptyItem(brandKey!), [brandKey]);
  const itemDiff = objectsAreDifferents(
    item,
    isCreate ? emptyItem : data?.qualifier,
  );
  const requiredFields = getRequiredErrorMessage<
    GetQualifierQuery_query_qualifier_Qualifier
  >(item, saveMandatoryFields);

  useEffect(() => {
    setItem(isCreate ? emptyItem : data?.qualifier || undefined);
  }, [isCreate, data, emptyItem, setItem]);

  const slugFormatError = isCreate && item?.slug ? validateSlug(item.slug) : '';

  const onSave = async () => {
    if (item) {
      setSaveLoading(true);
      const description = toDescriptionInput(item.description);
      const res = isCreate
        ? await createItem({
            title: item.title,
            slug: item.slug,
            description,
            status: item.status,
            brandKey: item.brandKey,
            format: item.format,
            type: item.type,
          })
        : await updateItem(item.id, {
            description,
            status: item.status,
            format: item.format,
            type: item.type,
          });
      setSaveLoading(false);
      if (res && isCreate) {
        history.replace(
          replaceParams(paths.QUALIFIER_EDIT, {
            id: (res.data as CreateQualifierMutation).createQualifier.id,
          }),
        );
      }
    }
  };

  return (
    <>
      <EditAppBar
        title={
          isCreate ? item?.title || 'Nouveau qualifier' : data?.qualifier?.title
        }
        onNavigateBack={() => history.push(paths.QUALIFIER_LIST)}
        save={{
          disabled: !itemDiff && !isCreate,
          loading: saveLoading,
          onClick: onSave,
          requiredFields,
        }}
      />
      <PageWithDrawer
        loading={loading}
        error={
          error ||
          (!item && !data?.qualifier
            ? Error("Ce qualifier n'existe pas")
            : undefined)
        }
        rightDrawer={
          <NonEditableField
            className={classes.field}
            label="Marque"
            value={item?.brandKey}
          />
        }
      >
        {item && (
          <>
            <TextField
              className={classes.field}
              disabled={!isCreate}
              label="Titre"
              onChange={({ target: { value } }) => {
                setItem((prev) => (prev ? { ...prev, title: value } : prev));
              }}
              value={item.title}
              variant="standard"
            />
            <TextField
              className={classes.field}
              disabled={!isCreate}
              error={!!slugFormatError}
              helperText={slugFormatError}
              label="Slug"
              onChange={({ target: { value } }) => {
                setItem((prev) =>
                  prev ? { ...prev, slug: value.toLowerCase() } : prev,
                );
              }}
              value={item.slug}
              variant="standard"
            />
            <TextField
              className={classes.field}
              label="Description"
              multiline
              onChange={({ target: { value } }) => {
                setItem((prev) =>
                  prev ? { ...prev, description: value || null } : prev,
                );
              }}
              value={item.description ?? ''}
              variant="standard"
            />
            <SimpleSelect
              label="Statut"
              className={classes.field}
              options={Object.values(QualifierStatus).map((qualifierStatus) => {
                return { value: qualifierStatus, label: qualifierStatus };
              })}
              value={item.status}
              onChange={(_, status) =>
                setItem((prev) =>
                  prev ? { ...prev, status: status as QualifierStatus } : prev,
                )
              }
            />
            <SimpleSelect
              label="Format"
              className={classes.field}
              options={Object.values(ArticleFormat).map((articleFormat) => {
                return { value: articleFormat, label: articleFormat };
              })}
              value={item.format}
              onChange={(_, format) =>
                setItem((prev) =>
                  prev ? { ...prev, format: format as ArticleFormat } : prev,
                )
              }
            />
            <SimpleSelect
              label="Type"
              className={classes.field}
              options={Object.values(QualifierType).map((qualifierType) => {
                return { value: qualifierType, label: qualifierType };
              })}
              value={item.type}
              onChange={(_, type) =>
                setItem((prev) =>
                  prev ? { ...prev, type: type as QualifierType } : prev,
                )
              }
            />
          </>
        )}
      </PageWithDrawer>
    </>
  );
};
